import React, { useState } from 'react';
import { Edit2, Trash2, ArrowUp, ArrowDown, Filter } from 'lucide-react';
import type { AnalyticsEntry, Platform } from '../../../types/analytics';
import { METRIC_TOOLTIPS } from '../../../types/analytics';

interface AnalyticsTableProps {
  entries: AnalyticsEntry[];
  onEdit: (entry: AnalyticsEntry) => void;
  onDelete: (id: string) => void;
}

type SortField = 'date' | 'views' | 'likes' | 'comments' | 'engagementRate';
type SortDirection = 'asc' | 'desc';

export default function AnalyticsTable({ entries, onEdit, onDelete }: AnalyticsTableProps) {
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [platformFilter, setPlatformFilter] = useState<Platform | 'all'>('all');

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedAndFilteredEntries = entries
    .filter(entry => platformFilter === 'all' || entry.platform === platformFilter)
    .sort((a, b) => {
      let comparison = 0;
      
      if (sortField === 'date') {
        comparison = new Date(b.date).getTime() - new Date(a.date).getTime();
      } else {
        comparison = b[sortField] - a[sortField];
      }

      return sortDirection === 'asc' ? -comparison : comparison;
    });

  const SortIcon = ({ field }: { field: SortField }) => {
    if (field !== sortField) return null;
    return sortDirection === 'asc' ? 
      <ArrowUp className="h-4 w-4" /> : 
      <ArrowDown className="h-4 w-4" />;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <div className="flex items-center space-x-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <select
            value={platformFilter}
            onChange={(e) => setPlatformFilter(e.target.value as Platform | 'all')}
            className="px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
          >
            <option value="all">Todas as plataformas</option>
            <option value="youtube">YouTube</option>
            <option value="instagram">Instagram</option>
            <option value="tiktok">TikTok</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {[
                { key: 'date', label: 'Data' },
                { key: 'platform', label: 'Plataforma' },
                { key: 'contentName', label: 'Conteúdo' },
                ...Object.entries(METRIC_TOOLTIPS).map(([key, { label }]) => ({
                  key,
                  label,
                  sortable: true
                }))
              ].map((column) => (
                <th
                  key={column.key}
                  onClick={() => column.sortable && handleSort(column.key as SortField)}
                  className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    column.sortable ? 'cursor-pointer hover:bg-gray-100' : ''
                  }`}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.label}</span>
                    {column.sortable && <SortIcon field={column.key as SortField} />}
                  </div>
                </th>
              ))}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedAndFilteredEntries.map((entry) => (
              <tr key={entry.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(entry.date).toLocaleDateString('pt-BR')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="capitalize">{entry.platform}</span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-900">
                      {entry.contentName}
                    </span>
                    {entry.screenshotPreview && (
                      <img
                        src={entry.screenshotPreview}
                        alt="Screenshot"
                        className="h-8 w-8 object-cover rounded-lg cursor-pointer hover:scale-150 transition-transform"
                        onClick={() => window.open(entry.screenshotPreview, '_blank')}
                      />
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-900">
                      {entry.views.toLocaleString()}
                    </span>
                    {entry.previousEntry && (
                      <span className={`text-xs ${
                        entry.views > entry.previousEntry.views ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {((entry.views - entry.previousEntry.views) / entry.previousEntry.views * 100).toFixed(1)}%
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-900">
                      {entry.likes.toLocaleString()}
                    </span>
                    {entry.previousEntry && (
                      <span className={`text-xs ${
                        entry.likes > entry.previousEntry.likes ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {((entry.likes - entry.previousEntry.likes) / entry.previousEntry.likes * 100).toFixed(1)}%
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-900">
                      {entry.comments.toLocaleString()}
                    </span>
                    {entry.previousEntry && (
                      <span className={`text-xs ${
                        entry.comments > entry.previousEntry.comments ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {((entry.comments - entry.previousEntry.comments) / entry.previousEntry.comments * 100).toFixed(1)}%
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-900">
                      {entry.engagementRate.toFixed(2)}%
                    </span>
                    {entry.previousEntry && (
                      <span className={`text-xs ${
                        entry.engagementRate > entry.previousEntry.engagementRate ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {((entry.engagementRate - entry.previousEntry.engagementRate) / entry.previousEntry.engagementRate * 100).toFixed(1)}%
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-3">
                    <button
                      onClick={() => onEdit(entry)}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => onDelete(entry.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}